import { defineMessages } from "react-intl";
import { GuestType } from "./GuestType.enum";
import { Translation } from "../../gen/witbooking/backoffice/occupation/v1/occupation_api_pb";
import { OccupationEntity } from "./domain/occupation.entity";

export const guestTypeTranslations = {
  [GuestType.SENIOR]: {
    sv: "seniorer",
    ru: "пожилые люди",
    pt: "idosos",
    nl: "senioren",
    ko: "어른",
    it: "gli anziani",
    fr: "les personnes âgées",
    eu: "seniors",
    es: "seniors",
    en: "seniors",
    de: "Senioren",
    da: "seniorer",
    ca: "gent gran",
    zh: "老年人",
    hu: "idősek",
  },
  [GuestType.ADULT]: {
    sv: "Vuxna",
    ru: "взрослых",
    pt: "adultos",
    nl: "volwassenen",
    ko: "성인",
    it: "adulti",
    fr: "adultes",
    eu: "Helduek",
    es: "adultos",
    en: "adults",
    de: "Erwachsener",
    da: "voksne",
    ca: "adults",
    zh: "成年人",
    hu: "felnőttek",
  },
  [GuestType.CHILD]: {
    sv: "barn",
    ru: "Детей",
    pt: "crianças",
    nl: "kinderen",
    ko: "아이들",
    it: "bambini",
    fr: "enfants",
    eu: "Haurrak",
    es: "niños",
    en: "children",
    de: "Kinder",
    da: "børn",
    ca: "nens",
    zh: "儿童",
    hu: "gyerekek",
  },
  [GuestType.TEENAGER]: {
    sv: "juniorer",
    ru: "подростков",
    pt: "adolescentes",
    nl: "tieners",
    ko: "십대들",
    it: "adolescenti",
    fr: "adolescents",
    eu: "nerabe",
    es: "juniors",
    en: "teenagers",
    de: "Jugendliche",
    da: "teenagere",
    ca: "adolescents",
    zh: "青少年",
    hu: "serdülők",
  },
  [GuestType.BABY]: {
    sv: "Spädbarn",
    ru: "Младенцы",
    pt: "bebês",
    nl: "baby's",
    ko: "아가들",
    it: "neonati",
    fr: "bébés",
    eu: "Haurtxo",
    es: "bebés",
    en: "babies",
    de: "Babys",
    da: "babyer",
    ca: "nadons",
    zh: "婴儿",
    hu: "babák",
  },
};

export const guestTypeSingularTranslations = {
  [GuestType.SENIOR]: {
    sv: "senior",
    ru: "старший",
    pt: "Senior",
    nl: "senior",
    ko: "연장자",
    it: "anziano",
    fr: "Sénior",
    eu: "senior",
    es: "senior",
    en: "senior",
    de: "Senior",
    da: "senior-",
    ca: "senior",
    zh: "老人",
    hu: "idős",
  },
  [GuestType.ADULT]: {
    sv: "vuxen",
    ru: "взрослый",
    pt: "adulto",
    nl: "volwassene",
    ko: "성인",
    it: "adulto",
    fr: "adulte",
    eu: "Helduen",
    es: "adulto",
    en: "adult",
    de: "Erwachsener",
    da: "voksen",
    ca: "adult",
    zh: "成年人",
    hu: "felnőtt",
  },
  [GuestType.CHILD]: {
    sv: "barn",
    ru: "ребенок",
    pt: "criança",
    nl: "kind",
    ko: "아이",
    it: "bambino",
    fr: "enfant",
    eu: "ume",
    es: "niño",
    en: "child",
    de: "Kind",
    da: "barn",
    ca: "nen",
    zh: "孩子",
    hu: "gyerek",
  },
  [GuestType.TEENAGER]: {
    sv: "junior",
    ru: "подросток",
    pt: "adolescente",
    nl: "tiener",
    ko: "십대",
    it: "adolescente",
    fr: "adolescent",
    eu: "nerabea",
    es: "junior",
    en: "teenager",
    de: "Teenager",
    da: "teenager",
    ca: "adolescent",
    zh: "青少年",
    hu: "serdülő",
  },
  [GuestType.BABY]: {
    sv: "bebis",
    ru: "малыш",
    pt: "bebê",
    nl: "baby",
    ko: "아가",
    it: "neonato",
    fr: "bébé",
    eu: "umea",
    es: "bebé",
    en: "baby",
    de: "Baby",
    da: "baby",
    ca: "nadó",
    zh: "婴儿",
    hu: "baba",
  },
};

export const guestTypeMessages = defineMessages({
  [GuestType.SENIOR]: {
    id: "shared.guestType.senior",
    description: "senior",
    defaultMessage: "seniors",
  },
  [GuestType.ADULT]: {
    id: "shared.guestType.adult",
    description: "adult",
    defaultMessage: "adults",
  },
  [GuestType.TEENAGER]: {
    id: "shared.guestType.teenager",
    description: "teenager",
    defaultMessage: "teenagers",
  },
  [GuestType.CHILD]: {
    id: "shared.guestType.child",
    description: "children",
    defaultMessage: "children",
  },
  [GuestType.BABY]: {
    id: "shared.guestType.baby",
    description: "babies",
    defaultMessage: "babies",
  },
});

export const singularGuestTypeMessages = defineMessages({
  [GuestType.SENIOR]: {
    id: "shared.guestType.singular.senior",
    description: "senior",
    defaultMessage: "senior",
  },
  [GuestType.ADULT]: {
    id: "shared.guestType.singular.adult",
    description: "adult",
    defaultMessage: "adult",
  },
  [GuestType.TEENAGER]: {
    id: "shared.guestType.singular.teenager",
    description: "teenager",
    defaultMessage: "teenager",
  },
  [GuestType.CHILD]: {
    id: "shared.guestType.singular.child",
    description: "child",
    defaultMessage: "child",
  },
  [GuestType.BABY]: {
    id: "shared.guestType.singular.baby",
    description: "baby",
    defaultMessage: "baby",
  },
});

export const occupationMessages = defineMessages({
  entityName: {
    id: "occupation.entityName",
    defaultMessage: "Occupation",
  },
  occupationTickerHelpText: {
    id: "occupation.ticker.help",
    defaultMessage: "Auto-generated",
  },
  occupationTranslationsTitle: {
    id: "occupation.translation.title",
    defaultMessage: "translations",
  },
  occupationTranslationNameLabel: {
    id: "occupation.translation.name",
    defaultMessage: "Name",
  },
});

export const tableHeaderMessages = defineMessages({
  ticker: { id: "occupation.ticker", defaultMessage: "Identifier" },
  translationsList: { id: "occupation.name", defaultMessage: "Name" },
  seniors: guestTypeMessages[GuestType.SENIOR],
  adults: guestTypeMessages[GuestType.ADULT],
  juniors: guestTypeMessages[GuestType.TEENAGER],
  children: guestTypeMessages[GuestType.CHILD],
  babies: guestTypeMessages[GuestType.BABY],
});

export function generateOccupationTicker(formData: OccupationEntity): string {
  return [
    { letter: "s", qty: formData.seniors },
    { letter: "a", qty: formData.adults },
    { letter: "j", qty: formData.juniors },
    { letter: "n", qty: formData.children },
    { letter: "b", qty: formData.babies },
  ]
    .filter(it => it.qty > 0)
    .map(it => `${it.qty}${it.letter}`)
    .join("");
}
export function generateOccupationTranslation(
  formData: OccupationEntity,
  allowedLocales: string[]
): Translation.AsObject[] {
  return allowedLocales.map(locale => {
    return {
      locale,
      description: "",
      name: [
        {
          translation:
            formData.seniors > 1
              ? guestTypeTranslations[GuestType.SENIOR][locale]
              : guestTypeSingularTranslations[GuestType.SENIOR][locale],
          qty: formData.seniors,
        },
        {
          translation:
            formData.adults > 1
              ? guestTypeTranslations[GuestType.ADULT][locale]
              : guestTypeSingularTranslations[GuestType.ADULT][locale],
          qty: formData.adults,
        },
        {
          translation:
            formData.juniors > 1
              ? guestTypeTranslations[GuestType.TEENAGER][locale]
              : guestTypeSingularTranslations[GuestType.TEENAGER][locale],
          qty: formData.juniors,
        },
        {
          translation:
            formData.children > 1
              ? guestTypeTranslations[GuestType.CHILD][locale]
              : guestTypeSingularTranslations[GuestType.CHILD][locale],
          qty: formData.children,
        },
        {
          translation:
            formData.babies > 1
              ? guestTypeTranslations[GuestType.BABY][locale]
              : guestTypeSingularTranslations[GuestType.BABY][locale],
          qty: formData.babies,
        },
      ]
        .filter(it => it.qty > 0)
        .map(it => `${it.qty} ${it.translation}`)
        .join(" + "),
    };
  });
}
